import React, {useEffect, useState} from "react";
import {IonCheckbox, IonCol, IonItem, IonRow} from "@ionic/react";
import {useTranslation} from "react-i18next";
import {StyledIonModal, StyledModalContent} from "@components/modal/modal.style";
import {StyledDataTable} from "@components/content/content.style";
import LoadingSkeleton from "@components/loading/loadingSkeleton.component";
import {ReactComponent as CloseIcon} from "@assets/images/e-remove.svg";
import {getAvailabilityPreferredPlacesOfService} from "@services/calendar/availability.service";
import {StyledButton} from "@components/button/button.style";
import {StyledFooter} from "@components/pane/pane.style";
import {AvailabilityPreferredPlaceOfService} from "@models/availability";
import {useToast} from "@context/toast.context";
import {StyledIonLabel} from "@components/form/input/label.style";
import {StyledIonCheckbox} from "@components/form/input/checkbox.style";

type PreferredPlacesOfServiceListModalProps = {
    isOpen: boolean;
    onClose: () => void;
    workerAvailabilityId?: number;
    preSelected?: number[];
    isAgreementForOuterPlaces?: boolean;
    setValue: (selected: any[], agreementForOuterPlaces: boolean) => void;
    disabled?: boolean;
};

const PreferredPlacesOfServiceListModal: React.FC<PreferredPlacesOfServiceListModalProps> = ({
                                                                                                 isOpen,
                                                                                                 onClose,
                                                                                                 workerAvailabilityId,
                                                                                                 preSelected = [],
                                                                                                 isAgreementForOuterPlaces = false,
                                                                                                 setValue,
                                                                                                 disabled
                                                                                             }) => {
    const {showToast} = useToast();
    const {t} = useTranslation();

    const [list, setList] = useState<AvailabilityPreferredPlaceOfService[]>([]);
    const [agreementForOuterPlaces, setAgreementForOuterPlaces] = useState<boolean>(isAgreementForOuterPlaces);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (isOpen) {
            fetchPreferredPlaces();
        }
    }, [isOpen]);

    const fetchPreferredPlaces = async () => {
        setLoading(true);

        try {
            const data = await getAvailabilityPreferredPlacesOfService(workerAvailabilityId);
            const listWithSelected = data.map((p: AvailabilityPreferredPlaceOfService) => ({
                ...p,
                selected: preSelected.includes(p.id),
            }));

            setList(listWithSelected);
        } finally {
            setLoading(false);
        }
    };

    const toggleSelection = (id: number) => {
        if (disabled) return;

        setList((prev) =>
            prev.map((item) => (item.id === id ? {...item, selected: !item.selected} : item))
        );
    };

    const allSelected = list.length > 0 && list.every((item) => item.selected);

    const toggleSelectAll = () => {
        if (disabled) return;

        const next = !allSelected;
        setList((prev) => prev.map((item) => ({...item, selected: next})));
    };

    const handleSave = () => {
        const selected = list.filter((item) => item.selected);

        if (selected.length === 0) {
            showToast({
                message: t('availability.error.selectAtLeastOnePlaceOfService'),
                type: 'danger',
                duration: 3000,
            });

            return;
        }

        setValue(selected, agreementForOuterPlaces);
        onClose();
    };

    return (
        <StyledIonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            initialBreakpoint={0.99}
            breakpoints={[0, 0.65, 0.99]}
        >
            <StyledModalContent className="modal-wrap t-p-50 scrollable">
                <CloseIcon className="close" onClick={onClose}/>
                <h1 className="title">{t("availability.preferredPlacesOfService.title")}</h1>
                {
                    loading && <LoadingSkeleton includeAvatar={false}/>
                }
                {
                    !loading && (
                        <>
                            <StyledDataTable className="no-margin">
                                {
                                    list.map((item) => (
                                        <div
                                            key={item.id}
                                            className="data-row"
                                            onClick={() => toggleSelection(item.id)}
                                            style={{display: "flex", justifyContent: "space-between", alignItems: "center"}}
                                        >
                                            <div className="data-icon-title titles">
                                                <div className="main-title" style={{fontSize: '15px'}}>{item.address || item.name}</div>
                                                {
                                                    item.address && <div className="sub-title">{item.name}</div>
                                                }
                                            </div>
                                            <IonCheckbox
                                                disabled={disabled}
                                                checked={item.selected}
                                                onClick={(e) => e.stopPropagation()}
                                                onIonChange={() => toggleSelection(item.id)}
                                            />
                                        </div>
                                    ))
                                }
                                <IonRow style={{width: "100%", marginTop: "15px"}}>
                                    <IonItem lines="none" style={{'--padding-start': '10px', '--inner-padding-start': '0', '--inner-padding-end': '0', 'width': '100%'}}>
                                        <StyledIonLabel className="ion-text-wrap">{t('availability.preferredPlacesOfService.selectAll')}</StyledIonLabel>
                                        <StyledIonCheckbox
                                            slot="start"
                                            disabled={disabled}
                                            onIonChange={toggleSelectAll}
                                            checked={allSelected}
                                            value={allSelected}
                                        />
                                    </IonItem>
                                </IonRow>
                                <IonRow style={{width: "100%"}}>
                                    <IonItem lines="none" style={{'--padding-start': '10px', '--inner-padding-start': '0', '--inner-padding-end': '0', 'width': '100%'}}>
                                        <StyledIonLabel className="ion-text-wrap">{t('availability.preferredPlacesOfService.agreementForOuterPlaces')}</StyledIonLabel>
                                        <StyledIonCheckbox
                                            slot="start"
                                            onIonChange={(e) => setAgreementForOuterPlaces(e.detail.checked)}
                                            checked={agreementForOuterPlaces}
                                            value={agreementForOuterPlaces}
                                        />
                                    </IonItem>
                                </IonRow>
                            </StyledDataTable>
                            {
                                !disabled && (
                                    <StyledFooter className="modal footer no-shadow" style={{padding: "0px"}}>
                                            <StyledButton onClick={handleSave}>
                                                {t("availability.savePreferredPlaces")}
                                            </StyledButton>
                                    </StyledFooter>
                                )
                            }
                        </>
                    )
                }
            </StyledModalContent>
        </StyledIonModal>
    );
};

export default React.memo(PreferredPlacesOfServiceListModal);